export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

export type InstallPlatform = "ios" | "android" | "other";

export type IosInstructionVariant = "ios18_plus" | "ios17" | "ios16_4_to_16_7" | "ios16_or_older";

export interface InstallPlatformInfo {
  platform: InstallPlatform;
  isSafari: boolean;
  isStandalone: boolean;
  iosVersionMajor: number | null;
  iosVersionMinor: number | null;
  iosInstructionVariant: IosInstructionVariant | null;
}

export function parseIosVersion(userAgent: string) {
  const match = /OS (\d+)[._](\d+)/i.exec(userAgent);
  if (!match) {
    return { major: null, minor: null };
  }

  const major = Number.parseInt(match[1], 10);
  const minor = Number.parseInt(match[2], 10);

  return {
    major: Number.isFinite(major) ? major : null,
    minor: Number.isFinite(minor) ? minor : null,
  };
}

export function detectInstallPlatform(
  userAgent: string,
  isStandalone: boolean,
): InstallPlatformInfo {
  const normalizedUa = userAgent.toLowerCase();
  const isAndroid = /android/i.test(userAgent);
  const isIos =
    /iphone|ipad|ipod/i.test(userAgent) ||
    (normalizedUa.includes("macintosh") && normalizedUa.includes("mobile"));
  const isSafari =
    isIos &&
    /safari/i.test(userAgent) &&
    !/crios|fxios|edgios|opios|duckduckgo|mercury|yabrowser/i.test(userAgent);

  if (isAndroid) {
    return {
      platform: "android",
      isSafari: false,
      isStandalone,
      iosVersionMajor: null,
      iosVersionMinor: null,
      iosInstructionVariant: null,
    };
  }

  if (!isIos) {
    return {
      platform: "other",
      isSafari: false,
      isStandalone,
      iosVersionMajor: null,
      iosVersionMinor: null,
      iosInstructionVariant: null,
    };
  }

  const { major, minor } = parseIosVersion(userAgent);

  let iosInstructionVariant: IosInstructionVariant = "ios16_or_older";
  if (major != null) {
    if (major >= 18) {
      iosInstructionVariant = "ios18_plus";
    } else if (major >= 17) {
      iosInstructionVariant = "ios17";
    } else if (major === 16 && (minor ?? 0) >= 4) {
      iosInstructionVariant = "ios16_4_to_16_7";
    }
  }

  return {
    platform: "ios",
    isSafari,
    isStandalone,
    iosVersionMajor: major,
    iosVersionMinor: minor,
    iosInstructionVariant,
  };
}

export function isStandaloneDisplayMode() {
  if (typeof window === "undefined") {
    return false;
  }

  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    navigatorWithStandalone.standalone === true
  );
}

export function formatIosVersionLabel(
  major: number | null,
  minor: number | null,
  variant: IosInstructionVariant | null,
) {
  if (major == null) {
    return "iOS";
  }

  if (variant === "ios16_or_older" && major <= 16) {
    return "iOS 16 o anterior";
  }

  if (minor == null) {
    return `iOS ${major}`;
  }

  return `iOS ${major}.${minor}`;
}
